export interface ConstraintRadioGroupProps<T extends string> {
    legend: string
    name: string
    options: readonly T[]
    value: string
    onChange(value: T): void
    /** 이 축에서 (선택 업종 + 나머지 축)과 실존 조합이 있는 값 집합. */
    enabledSet: Set<string>
    /** 매트릭스 로드 완료 여부. 미완료면 제약을 걸지 않는다. */
    ready: boolean
    /** 값별 보조 설명(있으면 2열 그리드 + 설명 텍스트로 렌더). */
    descriptions?: Record<string, string>
}

// 라디오 옵션 하나의 공통 클래스(비활성이면 흐리게·클릭 불가).
const radioClass = (disabled: boolean, extra = '') =>
    `border-border text-foreground flex flex-1 items-center gap-2 rounded-md border px-3 py-2 text-sm ${extra} ${
        disabled
            ? 'cursor-not-allowed opacity-40'
            : 'has-[:checked]:border-primary has-[:checked]:bg-accent cursor-pointer'
    }`

/**
 * 4축 상호제약 라디오 그룹 하나(규모·분류·단계 공용).
 * descriptions가 있으면 2열 그리드 + 설명 텍스트, 없으면 단순 가로 배치로 그린다.
 */
export function ConstraintRadioGroup<T extends string>({
    legend,
    name,
    options,
    value,
    onChange,
    enabledSet,
    ready,
    descriptions,
}: ConstraintRadioGroupProps<T>) {
    return (
        <fieldset className='space-y-1.5'>
            <legend className='text-foreground text-sm font-medium'>
                {legend}
            </legend>
            <div
                className={
                    descriptions ? 'grid gap-2 sm:grid-cols-2' : 'flex gap-2'
                }>
                {options.map((option) => {
                    const disabled = ready && !enabledSet.has(option)
                    return (
                        <label
                            key={option}
                            className={radioClass(
                                disabled,
                                descriptions ? 'items-start' : ''
                            )}>
                            <input
                                type='radio'
                                name={name}
                                value={option}
                                checked={value === option}
                                disabled={disabled}
                                onChange={() => onChange(option)}
                                className={
                                    descriptions
                                        ? 'accent-primary mt-0.5'
                                        : 'accent-primary'
                                }
                            />
                            {descriptions ? (
                                <span>
                                    <span className='font-medium'>
                                        {option}
                                    </span>
                                    <span className='text-muted-foreground mt-0.5 block text-xs'>
                                        {descriptions[option]}
                                    </span>
                                </span>
                            ) : (
                                option
                            )}
                        </label>
                    )
                })}
            </div>
        </fieldset>
    )
}
